import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Heart, Target } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TeamPage = () => {
  const [team, setTeam] = useState([]);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const response = await axios.get(`${API}/team`);
        setTeam(response.data);
      } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error("Error fetching team:", err);
      }
    };
    fetchTeam();
  }, []);

  const teamSpecialties = {
    jan: ["Management", "Opvolging", "Strategische Planning", "Community Building"],
    tibo: ["Personal Training", "Krachttraining", "Functionele Training", "Spieropbouw"],
    xenia: ["Personal Training", "Coaching", "Krachttraining", "Afvallen", "Fitness"],
  };

  return (
    <main data-testid="team-page" className="pt-20">
      {/* Hero Section */}
      <section className="section bg-[#0A0A0A]" data-testid="team-hero">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Ons Team</span>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Ontmoet Je
              <br />
              <span className="text-red-500">Coaches</span>
            </h1>
            <p className="text-zinc-400 text-lg">
              Achter elk succesverhaal staat een team van toegewijde
              professionals. Onze coaches combineren jarenlange ervaring met
              passie voor hun vak.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Team Group Photo */}
      <section className="bg-[#0A0A0A] pb-0" data-testid="team-group-photo">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative rounded-sm overflow-hidden border border-zinc-800">
              <img
                src="/team-group.jpeg"
                alt="Het Arena Gym Team"
                className="w-full max-h-[70vh] object-contain mx-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                <h2 className="text-3xl md:text-4xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
                  Jouw <span className="text-red-500">Team</span>
                </h2>
                <p className="text-zinc-300 text-sm mt-2">
                  Tibo, Xenia & Jan — samen klaar om jou te begeleiden.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Team Grid */}
      <section className="section bg-[#121212]" data-testid="team-grid">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 max-w-5xl mx-auto gap-8">
            {team.map((member, index) => (
              <motion.div
                key={member.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="group"
                data-testid={`team-member-${member.id}`}
              >
                <div className="relative aspect-[3/4] rounded-sm overflow-hidden border border-zinc-800 mb-6">
                  <img
                    src={member.image}
                    alt={member.naam}
                    className="w-full h-full object-cover filter grayscale group-hover:grayscale-0 transition-all duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />

                  {/* Specialties overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <div className="flex flex-wrap gap-2 mb-4 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                      {(teamSpecialties[member.id] || []).map(
                        (specialty, i) => (
                          <span
                            key={i}
                            className="text-xs bg-red-500/20 text-red-500 px-3 py-1 rounded-full"
                          >
                            {specialty}
                          </span>
                        )
                      )}
                    </div>
                    <h3 className="text-3xl font-black text-white font-['Barlow_Condensed'] uppercase">
                      {member.naam}
                    </h3>
                    <p className="text-red-500 text-sm uppercase tracking-wider">
                      {member.role}
                    </p>
                  </div>
                </div>

                <p className="text-zinc-400 leading-relaxed">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-[#0A0A0A]" data-testid="team-values">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="caption mb-4 block">Onze Aanpak</span>
            <h2 className="text-4xl md:text-5xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
              Wat Ons <span className="text-red-500">Drijft</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                icon: Award,
                title: "Professionaliteit",
                description:
                  "Gecertificeerde coaches met continue bijscholing. Wij blijven op de hoogte van de laatste ontwikkelingen in fitness en gezondheid.",
              },
              {
                icon: Heart,
                title: "Passie",
                description:
                  "Trainen is onze passie. Die energie en enthousiasme dragen we over op iedereen die bij ons traint.",
              },
              {
                icon: Target,
                title: "Resultaat",
                description:
                  "Jouw succes is ons succes. Wij stoppen niet totdat je je doelen hebt bereikt en overtroffen.",
              },
            ].map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`team-value-${index}`}
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-red-500" />
                </div>
                <h3 className="text-2xl font-black text-white mb-4 font-['Barlow_Condensed'] uppercase">
                  {value.title}
                </h3>
                <p className="text-zinc-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-[#121212]" data-testid="team-cta">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="caption mb-4 block">Klaar om te beginnen?</span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 font-['Barlow_Condensed'] uppercase tracking-tighter">
              Train Met De <span className="text-red-500">Beste</span>
            </h2>
            <p className="text-zinc-400 text-lg mb-10">
              Maak kennis met ons team tijdens een vrijblijvend intake gesprek.
              Samen bespreken we jouw doelen en maken we een plan.
            </p>
            <Link
              to="/boeken"
              data-testid="team-book-cta"
              className="btn-primary inline-flex items-center gap-2"
            >
              Boek Je Intake
              <ArrowRight size={18} />
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default TeamPage;
